<template>
    <div
        id="wallet-col-header"
        :style="gridStyle"
        class="grid items-center gap-4 bg-white dark:bg-slate-800 text-xs text-slate-700 z-20 w-full border-b border-slate-200 dark:border-slate-700"
    >
        <!-- Title column (not draggable) -->
        <div
            class="flex gap-4 justify-start items-center sticky left-0 z-20 bg-white dark:bg-slate-800 py-3 pl-6 h-full"
            :class="{ 'border-r border-slate-200 dark:border-slate-700': isScrolledLeft }"
        >
            <SortableColumnHeader
                label="Card"
                sortKey="card_title"
                :isActive="sortBy === 'card_title'"
                :sortable="isSortable('title')"
                justify="start"
                @sort="handleSort"
            />
        </div>
        <draggable
            :list="visibleOrderedColumns"
            tag="div"
            class="contents"
            handle=".drag-handle"
            @end="handleColumnReorder"
            item-key="id"
        >
            <template #item="{ element }">
                <div
                    class="shrink-0 flex items-center group hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors duration-150 rounded px-2 -mx-2 relative"
                    :class="getColumnAlignment(element.id)"
                >
                    <!-- Drag handle -->
                    <div
                        class="drag-handle opacity-0 group-hover:opacity-100 transition-opacity mr-2 absolute border-t border-l border-r border-slate-100 w-5 flex items-center justify-center top-0 left-1/2 -translate-y-full group-hover:bg-slate-50 dark:hover:bg-slate-700 rounded-t-lg"
                    >
                        <i class="fa-solid fa-grip-dots text-slate-500 dark:text-slate-400"></i>
                    </div>

                    <!-- Column header content -->
                    <div
                        class="flex-1 whitespace-nowrap flex"
                        :class="{
                            'justify-start': element.alignment === 'start',
                            'justify-end': element.alignment === 'end',
                            'justify-center': element.alignment === 'center',
                        }"
                    >
                        <SortableColumnHeader
                            :label="element.name"
                            :sortKey="element.id"
                            :isActive="sortBy === element.id"
                            :sortable="isSortable(element.id)"
                            :justify="element.alignment"
                            @sort="handleSort"
                        />

                        <!-- Special tooltip for 5/24 column -->
                        <Tooltip
                            v-if="element.id === 'chase_524'"
                            yPlacement="bottom"
                            xPlacement="left"
                            class="text-slate-500 dark:text-slate-400"
                        >
                            <template #trigger>
                                <i
                                    class="fa-regular fa-circle-exclamation -ml-10"
                                ></i>
                            </template>
                            <template #default>
                                Number of cards that contribute to your
                                <a
                                    class="link-base link-10x-orange !p-0 !text-xs"
                                    href="https://10xtravel.com/chase-5-24-rule/"
                                    target="_blank"
                                >
                                    5/24 count
                                </a>
                            </template>
                        </Tooltip>
                    </div>
                </div>
            </template>
        </draggable>

        <!-- Actions column (not draggable) -->
        <div
            class="w-[60px] flex gap-4 justify-end items-center sticky right-0 z-20 bg-white dark:bg-slate-800 py-3 h-full border-l border-slate-200 dark:border-slate-700"
        >
            <SortableColumnHeader
                :isActive="false"
                :sortable="false"
                justify="end"
                label=""
            />
        </div>
    </div>
</template>

<script setup>
import { computed } from 'vue';
import { useWalletStore } from '@/store/wallet';
import { storeToRefs } from 'pinia';
import draggable from 'vuedraggable';
import SortableColumnHeader from '@/components/ui/column-header/sortable-column-header.vue';
import Tooltip from '@/components/tooltip.vue';

const emit = defineEmits(['sort']);

const props = defineProps({
    isScrolledLeft: {
        type: Boolean,
        default: false,
    },
    gridStyle: {
        type: Object,
        default: () => ({}),
    },
});

const walletStore = useWalletStore();
const { orderedColumns, visibleColumns, sortBy, columns } =
    storeToRefs(walletStore);
const { updateColumnOrder } = walletStore;

// Get only the visible columns in their ordered sequence
const visibleOrderedColumns = computed(() => {
    return orderedColumns.value.filter(
        (col) =>
            visibleColumns.value.includes(col.id) &&
            col.id !== 'actions' &&
            col.id !== 'title'
    );
});

const isSortable = (columnId) => {
    const sortableColumns = [
        'title',
        'institution_name',
        'card_type',
        'user_name',
        'account_type',
        'card_status',
        'date_opened',
        'annual_fee_computed',
        'date_closed',
        'credit_limit',
    ];
    return sortableColumns.includes(columnId);
};

// Get text alignment class based on column's alignment property
const getColumnAlignment = (columnId) => {
    // Find the column in the store
    const column = columns.value.find((col) => col.id === columnId);
    if (!column) return '';

    // Map alignment property to tailwind class
    switch (column.alignment) {
        case 'right':
            return 'text-right';
        case 'center':
            return 'text-center';
        case 'left':
        default:
            return 'text-left';
    }
};

// Event handlers
const handleColumnReorder = () => {
    // Update the column order in the store
    try {
        if (
            visibleOrderedColumns.value &&
            visibleOrderedColumns.value.length > 0
        ) {
            updateColumnOrder(visibleOrderedColumns.value);
        }
    } catch (error) {
        // Graceful error handling for column reordering
        if (process.env.NODE_ENV === 'development') {
            console.error('Error updating column order:', error);
        }
    }
};

const handleSort = (direction, column) => {
    emit('sort', direction, column);
};
</script>

<style scoped>
.drag-handle:hover {
    cursor: grab;
}

.drag-handle:active {
    cursor: grabbing;
}

/* Dragging state styles */
:deep(.sortable-ghost) {
    opacity: 0.5;
}

:deep(.sortable-chosen) {
    cursor: grabbing;
}

:deep(.sortable-drag) {
    cursor: grabbing;
    transform: rotate(5deg);
}
</style>
